import { useCallback, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import type { EventTypeRow } from '@/db/types'
import {
  archiveEventType,
  CatalogGuardError,
  countEntriesForType,
  deleteEventType,
  listCategories,
  listEventTypes,
  mergeEventTypes,
  updateEventType,
} from '@/core/repository/catalogRepository'
import { listObjects } from '@/core/repository/objectRepository'
import { labelColorOn } from '@/design/contrast'
import { logger } from '@/core/logging/logger'
import { ColorField } from './ColorField'

interface TypeEditorProps {
  type: EventTypeRow
  onClose: () => void
  onChanged: () => void
}

type Confirm = 'none' | 'delete' | 'merge'

/**
 * Editing one event type.
 *
 * Saves on close rather than on every keystroke: each write is a synced row,
 * and typing a five-letter name should not produce five rows to push.
 */
export function TypeEditor({ type, onClose, onChanged }: TypeEditorProps) {
  const [name, setName] = useState(type.name)
  const [shortcut, setShortcut] = useState(type.shortcutText)
  const [colorHex, setColorHex] = useState(type.colorHex)
  const [categoryId, setCategoryId] = useState<string | null>(type.categoryId)
  const [defaultObjectId, setDefaultObjectId] = useState<string | null>(type.defaultObjectId)
  const [pinned, setPinnedDraft] = useState(type.isPinned === 1)
  const [confirm, setConfirm] = useState<Confirm>('none')
  const [mergeTarget, setMergeTarget] = useState('')
  const [error, setError] = useState<string | null>(null)

  const categories = useLiveQuery(() => listCategories(true), [], [])
  const allTypes = useLiveQuery(() => listEventTypes(true), [], [])
  const objects = useLiveQuery(() => listObjects(), [], [])
  const usage = useLiveQuery(() => countEntriesForType(type.id), [type.id], 0)

  const mergeCandidates = useMemo(
    () => allTypes.filter((t) => t.id !== type.id),
    [allTypes, type.id],
  )

  const previewLabel = shortcut.trim() || name.trim() || 'Event'

  const save = useCallback(async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError('An event type needs a name.')
      return
    }

    await updateEventType(type.id, {
      name: trimmed,
      // An empty shortcut falls back to the name at render time, so storing
      // the name here would silently freeze the shortcut the next time the
      // name changes.
      shortcutText: shortcut.trim(),
      colorHex,
      categoryId,
      defaultObjectId,
      isPinned: pinned ? 1 : 0,
    })
    onChanged()
    onClose()
  }, [
    name,
    shortcut,
    colorHex,
    categoryId,
    defaultObjectId,
    pinned,
    type.id,
    onChanged,
    onClose,
  ])

  const handleArchive = useCallback(async () => {
    await archiveEventType(type.id, type.isArchived === 0)
    onChanged()
    onClose()
  }, [type.id, type.isArchived, onChanged, onClose])

  const handleDelete = useCallback(async () => {
    try {
      await deleteEventType(type.id)
      onChanged()
      onClose()
    } catch (err: unknown) {
      if (err instanceof CatalogGuardError) {
        setConfirm('none')
        setError(
          `${err.message} Archive it to keep the history, or merge it into another type.`,
        )
        return
      }
      logger.error('db', 'Could not delete event type', err)
      setError('Could not delete that event type.')
    }
  }, [type.id, onChanged, onClose])

  const handleMerge = useCallback(async () => {
    if (!mergeTarget) return
    try {
      await mergeEventTypes(type.id, mergeTarget)
      onChanged()
      onClose()
    } catch (err: unknown) {
      logger.error('db', 'Could not merge event types', err)
      setError('Could not merge those event types.')
    }
  }, [mergeTarget, type.id, onChanged, onClose])

  return (
    <div className="mg-sheet mg-sheet--over" role="dialog" aria-label={`Edit ${type.name}`}>
      <header className="mg-head">
        <button className="mg-head-btn" onClick={onClose}>
          Cancel
        </button>
        <h2 className="mg-title">Event type</h2>
        <button className="mg-head-btn mg-head-btn--strong" onClick={() => void save()}>
          Save
        </button>
      </header>

      {error ? (
        <p className="mg-status mg-status--error" role="status">
          {error}
        </p>
      ) : null}

      <div className="mg-body scroll-y">
        <section className="mg-section">
          <label className="mg-field-label" htmlFor="mg-type-name">
            Name
          </label>
          <input
            id="mg-type-name"
            className="mg-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Focus"
          />

          <label className="mg-field-label" htmlFor="mg-type-shortcut">
            Short label on the block
          </label>
          <input
            id="mg-type-shortcut"
            className="mg-input"
            value={shortcut}
            onChange={(e) => setShortcut(e.target.value)}
            placeholder={name || 'Focus'}
            maxLength={12}
          />
          <p className="mg-hint">
            A block is about a centimetre tall. Leave this empty to use the full name.
          </p>

          {/* The real thing to judge against: a block, at block size, in the
              chosen colour, with the computed label colour on it. */}
          <div className="mg-block-preview" aria-hidden="true">
            <span className="mg-block-gutter">09:00</span>
            <span className="mg-block" style={{ background: colorHex, color: labelColorOn(colorHex) }}>
              {previewLabel}
            </span>
          </div>
        </section>

        <section className="mg-section">
          <h3 className="mg-label">Colour</h3>
          <ColorField value={colorHex} onChange={setColorHex} sampleText={previewLabel} />
        </section>

        <section className="mg-section">
          <label className="mg-field-label" htmlFor="mg-type-category">
            Category
          </label>
          <select
            id="mg-type-category"
            className="mg-input"
            value={categoryId ?? ''}
            onChange={(e) => setCategoryId(e.target.value || null)}
          >
            <option value="">Uncategorised</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <label className="mg-field-label" htmlFor="mg-type-object">
            Attach an object automatically
          </label>
          <select
            id="mg-type-object"
            className="mg-input"
            value={defaultObjectId ?? ''}
            onChange={(e) => setDefaultObjectId(e.target.value || null)}
          >
            <option value="">None</option>
            {objects.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>

          <label className="mg-toggle">
            <input
              type="checkbox"
              checked={pinned}
              onChange={(e) => setPinnedDraft(e.target.checked)}
            />
            <span>Pin above the categories</span>
          </label>
        </section>

        <section className="mg-section">
          <h3 className="mg-label">History</h3>
          <p className="mg-hint">
            {usage === 0
              ? 'No blocks use this event type yet.'
              : `${usage} block${usage === 1 ? '' : 's'} use this event type.`}
          </p>

          <button className="mg-btn" onClick={() => void handleArchive()}>
            {type.isArchived === 1 ? 'Bring back into the picker' : 'Archive'}
          </button>
          <p className="mg-hint">
            Archiving removes it from the picker and keeps every block and every statistic.
          </p>

          {confirm === 'merge' ? (
            <div className="mg-inline-form">
              <label className="mg-field-label" htmlFor="mg-merge-target">
                Move all blocks to
              </label>
              <select
                id="mg-merge-target"
                className="mg-input"
                value={mergeTarget}
                onChange={(e) => setMergeTarget(e.target.value)}
              >
                <option value="">Choose an event type…</option>
                {mergeCandidates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <button
                className="mg-btn mg-btn--danger"
                onClick={() => void handleMerge()}
                disabled={!mergeTarget}
              >
                Merge and delete “{type.name}”
              </button>
              <button className="mg-btn" onClick={() => setConfirm('none')}>
                Cancel
              </button>
            </div>
          ) : (
            <button className="mg-btn" onClick={() => setConfirm('merge')}>
              Merge into another event type…
            </button>
          )}

          <button
            className="mg-btn mg-btn--danger"
            onClick={() => (confirm === 'delete' ? void handleDelete() : setConfirm('delete'))}
          >
            {confirm === 'delete' ? 'Tap again to delete' : 'Delete'}
          </button>
          {usage > 0 ? (
            <p className="mg-hint">
              Delete is refused while blocks still use this. Archive or merge instead.
            </p>
          ) : null}
        </section>
      </div>
    </div>
  )
}
